/**
 * Infinite Scroll para Colecciones de Shopify Dawn
 * Maneja la carga automática de productos al hacer scroll
 * Compatible con el sistema de facets/filtros
 */
class InfiniteScroll {
  constructor() {
    this.isLoading = false;
    this.hasMorePages = true;
    this.currentPage = 1;
    this.scrollThreshold = 800; // píxeles antes del final para activar carga
    this.debounceDelay = 100; // ms para debounce del scroll
    this.scrollTimeout = null;

    this.init();
  }

  init() {
    // Esperar a que el DOM esté listo
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.productGrid = document.getElementById("product-grid");
    this.paginationWrapper = document.querySelector(".pagination-wrapper");

    if (!this.productGrid) {
      console.warn("InfiniteScroll: No se encontró el grid de productos");
      return;
    }

    // Ocultar paginación visualmente pero mantenerla en DOM para SEO
    if (this.paginationWrapper) {
      this.paginationWrapper.classList.add("pagination-wrapper--hidden");
    }

    // Configurar listener de scroll con debounce
    this.setupScrollListener();

    // Configurar observer para detectar cambios en el DOM (cuando facets actualiza)
    this.setupMutationObserver();

    // Verificar si hay más páginas disponibles
    this.checkForMorePages();

    console.log("InfiniteScroll: Inicializado correctamente");
  }

  setupScrollListener() {
    window.addEventListener("scroll", () => {
      // Debounce para optimizar rendimiento
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.handleScroll();
      }, this.debounceDelay);
    });
  }

  setupMutationObserver() {
    // Observar cambios en el ProductGridContainer para detectar actualizaciones de facets
    const container = document.getElementById("ProductGridContainer");
    if (!container) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.target.id === "ProductGridContainer") {
          // El grid fue reemplazado por facets.js, reinicializar
          setTimeout(() => {
            this.reinitialize();
          }, 100);
        }
      });
    });

    this.observer.observe(container, {
      childList: true,
      subtree: true,
    });
  }

  handleScroll() {
    if (this.isLoading || !this.hasMorePages) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - this.scrollThreshold;

    if (isNearBottom) {
      this.loadMoreProducts();
    }
  }

  async loadMoreProducts() {
    if (this.isLoading || !this.hasMorePages) return;

    const nextButton = document.querySelector(".pagination__item--prev");
    if (!nextButton) {
      this.hasMorePages = false;
      return;
    }

    const nextUrl = nextButton.getAttribute("href");
    if (!nextUrl) {
      this.hasMorePages = false;
      return;
    }

    this.isLoading = true;
    this.showLoadingSpinner();

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Extraer productos del HTML de respuesta
      const newProducts = this.extractProductsFromHTML(doc);

      if (newProducts.length === 0) {
        this.hasMorePages = false;
        this.hideLoadingSpinner();
        return;
      }

      // Agregar productos al grid existente
      this.appendProductsToGrid(newProducts);

      // Actualizar estado de paginación
      this.updatePaginationState(doc);

      this.currentPage++;
    } catch (error) {
      console.error("Error al cargar más productos:", error);
      this.showErrorMessage();
    } finally {
      this.isLoading = false;
      this.hideLoadingSpinner();
    }
  }

  extractProductsFromHTML(doc) {
    // Buscar productos en el HTML de respuesta
    const productGrid = doc.getElementById("product-grid");
    if (!productGrid) return [];

    return Array.from(productGrid.querySelectorAll(".grid__item"));
  }

  appendProductsToGrid(newProducts) {
    // Obtener referencia del primer producto para mantener clases consistentes
    const referenceProduct = this.productGrid.querySelector(".grid__item");

    newProducts.forEach((product, index) => {
      // Clonar el elemento para poder manipularlo
      const clonedProduct = product.cloneNode(true);

      // Mantener clases consistentes con productos existentes
      if (referenceProduct) {
        clonedProduct.className = referenceProduct.className;
      }

      // Agregar animación de entrada si está habilitada
      if (window.Shopify && window.Shopify.designMode) {
        // En el editor de temas, no agregar animaciones
      } else if (typeof initializeScrollAnimationTrigger === "function") {
        // Usar el sistema de animaciones del theme
        clonedProduct.classList.add("scroll-trigger", "animate--slide-in");
        clonedProduct.style.setProperty("--animation-order", this.productGrid.children.length + index + 1);
      }

      this.productGrid.appendChild(clonedProduct);
    });

    // Reinicializar animaciones si están disponibles
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger();
    }
  }

  updatePaginationState(doc) {
    const newNextButton = doc.querySelector(".pagination__item--prev");
    const currentNextButton = document.querySelector(".pagination__item--prev");

    if (newNextButton && currentNextButton) {
      // Actualizar URL del botón siguiente
      currentNextButton.setAttribute("href", newNextButton.getAttribute("href"));
    } else {
      // No hay más páginas
      this.hasMorePages = false;
      if (currentNextButton) {
        currentNextButton.remove();
      }
    }
  }

  showLoadingSpinner() {
    // Crear spinner personalizado para scroll infinito
    let spinner = document.getElementById("infinite-scroll-spinner");
    if (!spinner) {
      spinner = document.createElement("div");
      spinner.id = "infinite-scroll-spinner";
      spinner.className = "infinite-scroll-spinner";
      spinner.innerHTML = `
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;

      // Insertar después del grid de productos
      if (this.productGrid && this.productGrid.parentNode) {
        this.productGrid.parentNode.insertBefore(spinner, this.productGrid.nextSibling);
        console.log("InfiniteScroll: Spinner creado y agregado al DOM");
      } else {
        console.error("InfiniteScroll: No se pudo insertar el spinner - productGrid o parentNode no encontrado");
        return;
      }
    }

    spinner.style.display = "block";
    console.log("InfiniteScroll: Spinner mostrado");
  }

  hideLoadingSpinner() {
    const spinner = document.getElementById("infinite-scroll-spinner");
    if (spinner) {
      spinner.style.display = "none";
      console.log("InfiniteScroll: Spinner ocultado");
    }
  }

  showErrorMessage() {
    // Crear mensaje de error temporal
    const errorDiv = document.createElement("div");
    errorDiv.className = "infinite-scroll-error";
    errorDiv.style.cssText = `
      text-align: center;
      padding: 20px;
      background: #f8f8f8;
      border: 1px solid #ddd;
      margin: 20px 0;
      border-radius: 4px;
      color: #666;
    `;
    errorDiv.textContent = "Error al cargar más productos. Intenta recargar la página.";

    this.productGrid.parentNode.insertBefore(errorDiv, this.productGrid.nextSibling);

    // Remover mensaje después de 5 segundos
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  checkForMorePages() {
    const nextButton = document.querySelector(".pagination__item--prev");
    this.hasMorePages = !!nextButton;
  }

  reinitialize() {
    console.log("InfiniteScroll: Reinicializando después de cambio en facets");

    // Limpiar estado anterior
    this.isLoading = false;
    this.currentPage = 1;

    // Reconfigurar elementos del DOM
    this.productGrid = document.getElementById("product-grid");
    this.paginationWrapper = document.querySelector(".pagination-wrapper");

    if (!this.productGrid) {
      console.warn("InfiniteScroll: No se encontró el grid después de reinicialización");
      return;
    }

    // Ocultar paginación nuevamente
    if (this.paginationWrapper) {
      this.paginationWrapper.classList.add("pagination-wrapper--hidden");
    }

    // Verificar si hay más páginas
    this.checkForMorePages();

    // Limpiar spinner si existe
    this.hideLoadingSpinner();
  }

  destroy() {
    // Limpiar listeners y observers
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    // Mostrar paginación nuevamente
    if (this.paginationWrapper) {
      this.paginationWrapper.classList.remove("pagination-wrapper--hidden");
    }

    // Limpiar spinner
    this.hideLoadingSpinner();

    console.log("InfiniteScroll: Destruido");
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.infiniteScroll = new InfiniteScroll();
  });
} else {
  window.infiniteScroll = new InfiniteScroll();
}

// Exportar para uso global si es necesario
window.InfiniteScroll = InfiniteScroll;
